import { useEffect, useState } from "react";
import { ArrowLeft, CheckCircle, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { apiError } from "../lib/api";
import { STATUS_STYLE, STATUS_LABEL, fmtMin, fmtDrift } from "./Passe";

export default function KioskPasse({ session, kioskApi, onExit }) {
  const [tables, setTables] = useState(null);
  const [presets, setPresets] = useState([]);
  const [label, setLabel] = useState("");
  const [presetId, setPresetId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => kioskApi("get", "/kiosk/pacing/board").then((r) => setTables(r.data)).catch(() => {});
  useEffect(() => {
    load();
    kioskApi("get", "/kiosk/pacing/presets").then((r) => {
      setPresets(r.data);
      if (r.data.length) setPresetId(r.data[0].id);
    }).catch(() => {});
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      load();
    } catch (e) {
      toast.error(apiError(e, "Action failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="kiosk-passe">
      <button onClick={onExit} data-testid="kiosk-passe-back" className="flex items-center gap-2 text-[#BDB8CC] hover:text-white transition-colors">
        <ArrowLeft size={20} /> Back
      </button>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold">Passé board</h1>
        <p className="text-[#79728C] text-xs">{session.employee?.first_name} · refreshes every 15s</p>
      </div>

      <div className="glass rounded-2xl p-4 flex flex-wrap items-center gap-3">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Table (e.g. T4)" data-testid="kiosk-passe-label"
          className="bg-[#07050A] border border-white/10 rounded-xl px-4 py-3 text-white w-36 outline-none focus:border-[#9D5CFF]" />
        <select value={presetId} onChange={(e) => setPresetId(e.target.value)} data-testid="kiosk-passe-preset"
          className="bg-[#07050A] border border-white/10 rounded-xl px-4 py-3 text-white flex-1 min-w-[180px] outline-none">
          {presets.map((p) => <option key={p.id} value={p.id}>{`${p.name} · ${p.courses.length} courses · ${fmtMin(p.total_min)}`}</option>)}
        </select>
        <button disabled={busy || !label.trim() || !presetId} data-testid="kiosk-passe-open"
          onClick={() => act(() => kioskApi("post", "/kiosk/pacing/open", { label, preset_id: presetId }), `Table ${label} opened`)}
          className="rounded-xl px-5 py-3 bg-[#9D5CFF] text-white font-semibold active:scale-95 transition disabled:opacity-40 flex items-center gap-2">
          <Plus size={18} weight="bold" /> Open
        </button>
      </div>

      {tables && tables.length === 0 && (
        <p className="text-center text-[#79728C] py-8" data-testid="kiosk-passe-empty">No open tables right now.</p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {(tables || []).map((t) => {
          const allServed = t.state.current_idx === null;
          return (
            <div key={t.id} className="glass rounded-2xl p-4 space-y-3" data-testid={`kiosk-passe-card-${t.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-heading text-xl font-bold">{t.label}</p>
                <div className="flex items-center gap-2">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLE[t.state.status]}`} data-testid={`kiosk-passe-status-${t.id}`}>
                    {STATUS_LABEL[t.state.status]}
                  </span>
                  {!t.courses_log?.length && (
                    <button onClick={() => act(() => kioskApi("delete", `/kiosk/pacing/${t.id}`), "Table voided")} data-testid={`kiosk-passe-void-${t.id}`}
                      className="text-[#79728C] hover:text-[#F87171] transition-colors"><X size={16} /></button>
                  )}
                </div>
              </div>
              <p className="text-sm text-[#BDB8CC]" data-testid={`kiosk-passe-course-${t.id}`}>
                {allServed ? "All courses served" : `Course ${t.state.current_pos} — ${t.state.current_name}`}
                <span className="text-[#79728C]"> · {fmtMin(t.state.elapsed_min)} elapsed · drift {fmtDrift(t.state.drift_min)}</span>
              </p>
              {allServed ? (
                <button disabled={busy} onClick={() => act(() => kioskApi("post", `/kiosk/pacing/${t.id}/close`), "Table closed — totals logged")}
                  data-testid={`kiosk-passe-close-${t.id}`}
                  className="w-full rounded-xl py-4 bg-[#A9C97E] text-black font-heading font-bold text-lg active:scale-95 transition disabled:opacity-40 flex items-center justify-center gap-2">
                  <CheckCircle size={22} weight="bold" /> Close table
                </button>
              ) : (
                <button disabled={busy} onClick={() => act(() => kioskApi("post", `/kiosk/pacing/${t.id}/serve`))}
                  data-testid={`kiosk-passe-serve-${t.id}`}
                  className="w-full rounded-xl py-4 bg-[#9D5CFF] text-white font-heading font-bold text-lg active:scale-95 transition disabled:opacity-40 flex items-center justify-center gap-2">
                  <CheckCircle size={22} weight="bold" /> Course served →
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
